import { tiers } from '../data/tiers';
import type { Expert } from '../types/expert';
import type { Privilege, ScoreFactor, Tier, TierId } from '../types/tier';
import { averageRating, engagementCount } from './expert';

const WEIGHTS = { engagements: 35, feedback: 25, social: 25, tenure: 15 };
const ENGAGEMENT_TARGET = 10;
const TENURE_TARGET_YEARS = 5;

export function tenureYears(expert: Expert): number {
  return (Date.now() - new Date(expert.memberSince).getTime()) / (365.25 * 86400000);
}

export function tierBreakdown(expert: Expert): ScoreFactor[] {
  const count = engagementCount(expert);
  const rating = averageRating(expert);
  const years = tenureYears(expert);
  return [
    {
      key: 'engagements',
      label: 'Engagements',
      points: Math.min(count / ENGAGEMENT_TARGET, 1) * WEIGHTS.engagements,
      max: WEIGHTS.engagements,
      detail: `${count} completed of ${ENGAGEMENT_TARGET} target`,
    },
    {
      key: 'feedback',
      label: 'Feedback score',
      points: (rating / 5) * WEIGHTS.feedback,
      max: WEIGHTS.feedback,
      detail: `${rating.toFixed(1)} / 5 average rating`,
    },
    {
      key: 'social',
      label: 'Social influence',
      points: (expert.social.overallScore / 100) * WEIGHTS.social,
      max: WEIGHTS.social,
      detail: `${expert.social.overallScore} / 100 AI social score`,
    },
    {
      key: 'tenure',
      label: 'Tenure',
      points: Math.min(years / TENURE_TARGET_YEARS, 1) * WEIGHTS.tenure,
      max: WEIGHTS.tenure,
      detail: `${years.toFixed(1)} years in the network`,
    },
  ];
}

export function tierScore(expert: Expert): number {
  const total = tierBreakdown(expert).reduce((s, f) => s + f.points, 0);
  return Math.round(total * 10) / 10;
}

export function tierForScore(score: number): Tier {
  return [...tiers].reverse().find((t) => score >= t.minScore) ?? tiers[0];
}

export function getTier(expert: Expert): Tier {
  return tierForScore(tierScore(expert));
}

export function nextTier(tier: Tier): Tier | undefined {
  return tiers.find((t) => t.level === tier.level + 1);
}

export function unlockedPrivileges(tier: Tier): Privilege[] {
  return tiers.filter((t) => t.level <= tier.level).flatMap((t) => t.privileges);
}

export type TierStyle = {
  badge: string;
  solid: string;
  soft: string;
  text: string;
  bar: string;
};

export const tierStyles: Record<TierId, TierStyle> = {
  associate: {
    badge: 'border-orange-200 bg-orange-50 text-orange-800',
    solid: 'bg-orange-700 text-white',
    soft: 'bg-orange-50',
    text: 'text-orange-800',
    bar: 'bg-orange-600',
  },
  fellow: {
    badge: 'border-slate-300 bg-slate-100 text-slate-700',
    solid: 'bg-slate-500 text-white',
    soft: 'bg-slate-50',
    text: 'text-slate-700',
    bar: 'bg-slate-500',
  },
  distinguished: {
    badge: 'border-amber-300 bg-amber-50 text-amber-800',
    solid: 'bg-amber-500 text-white',
    soft: 'bg-amber-50',
    text: 'text-amber-800',
    bar: 'bg-amber-500',
  },
  ambassador: {
    badge: 'border-neutral-800 bg-neutral-900 text-amber-300',
    solid: 'bg-neutral-900 text-amber-300',
    soft: 'bg-neutral-900',
    text: 'text-amber-300',
    bar: 'bg-neutral-900',
  },
};
